import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import {
  SpotifyPlaylist, getSpotifyPlaylists, getSpotifyPlaylistDurations, addTracksToSpotifyPlaylist,
} from '../../services/sonosApi';
import { TrackArt } from './sonosTrackUi';
import { spotifyErrorMessage } from './spotifyRateLimit';
import { formatTotalDuration } from './sonosDuration';
import { SONOS_BLUE } from './sonosUiStyles';

interface AddToPlaylistDialogProps {
  open: boolean;
  trackUri: string | null;
  onClose: () => void;
  onAdded?: () => void;
  onError?: (msg: string) => void;
}

const AddToPlaylistDialog: React.FC<AddToPlaylistDialogProps> = ({
  open,
  trackUri,
  onClose,
  onAdded,
  onError,
}) => {
  const { t } = useTranslation();
  const requestClose = useOverlayBackButton(open, onClose, 'sonos-add-to-playlist');

  const errMsg = (e: any): string => spotifyErrorMessage(e, t, 'musicSystem.playback.playError');
  const [playlists, setPlaylists] = useState<SpotifyPlaylist[]>([]);
  const [durations, setDurations] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [durationsLoading, setDurationsLoading] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  // Refs para no re-disparar la carga cuando el padre pasa callbacks inline
  // (cambian de identidad en cada render y provocarían un loop de recarga).
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!open) {
      setPlaylists([]);
      setDurations({});
      return;
    }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const list = await getSpotifyPlaylists();
        if (cancelled) return;
        setPlaylists(list);
        setLoading(false);
        if (list.length === 0) return;
        setDurationsLoading(true);
        const map = await getSpotifyPlaylistDurations(list.map((p) => p.id));
        if (!cancelled) setDurations(map);
      } catch (e: any) {
        if (!cancelled) {
          onErrorRef.current?.(errMsg(e));
          setPlaylists([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setDurationsLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // Solo re-cargar al abrir/cerrar; onError/t via ref para evitar loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePick = async (playlistId: string) => {
    if (!trackUri || addingId) return;
    setAddingId(playlistId);
    try {
      await addTracksToSpotifyPlaylist(playlistId, [trackUri]);
      onAdded?.();
      requestClose();
    } catch (e: any) {
      onError?.(errMsg(e));
    } finally {
      setAddingId(null);
    }
  };

  const metaLine = (pl: SpotifyPlaylist) => {
    const parts: string[] = [];
    if (pl.tracks != null && pl.tracks > 0) {
      parts.push(t('musicSystem.library.trackCount', { count: pl.tracks }));
    }
    const dur = durations[pl.id];
    if (dur && dur > 0) {
      const label = formatTotalDuration(dur);
      if (label) parts.push(label);
    } else if (durationsLoading) {
      parts.push('…');
    }
    if (pl.owner) parts.push(pl.owner);
    return parts.join(' · ');
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1800 }}
      PaperProps={{
        sx: {
          bgcolor: '#121212',
          color: '#fff',
          borderRadius: 2,
          maxHeight: '78vh',
          m: 1,
        },
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.25,
        px: 1.5,
        borderBottom: '1px solid #2a2a2a',
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
          {t('musicSystem.search.addToPlaylist')}
        </Typography>
        <IconButton onClick={requestClose} sx={{ color: '#aaa', p: 0.5 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 0, py: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: SONOS_BLUE }} />
          </Box>
        ) : playlists.length === 0 ? (
          <Typography sx={{ color: '#999', fontSize: 13, py: 3, px: 2, textAlign: 'center' }}>
            {t('musicSystem.playback.noPlaylists')}
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 'calc(78vh - 56px)', overflowY: 'auto' }}>
            {playlists.map((pl) => (
              <Box
                key={pl.id}
                onClick={() => { void handlePick(pl.id); }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.1,
                  borderBottom: '1px solid #222',
                  cursor: addingId ? 'default' : 'pointer',
                  opacity: addingId && addingId !== pl.id ? 0.5 : 1,
                  '&:hover': addingId ? undefined : { bgcolor: 'rgba(35,145,255,0.08)' },
                }}
              >
                <TrackArt url={pl.image_url} size={52} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {pl.name}
                  </Typography>
                  <Typography sx={{
                    color: 'rgba(255,255,255,0.62)',
                    fontSize: 12,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {metaLine(pl)}
                  </Typography>
                </Box>
                {addingId === pl.id && (
                  <CircularProgress size={20} sx={{ color: SONOS_BLUE, flexShrink: 0 }} />
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddToPlaylistDialog;
